import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  receiveOTP,
  verifyOTP,
  sendLoginOTP,
  loginWithOTP,
  createUser,
  base_url,
  getPublicAppSettings,
} from '../../api/apiServices';
import { useAuth } from '../../context/useAuth';
import { useAnalytics } from '../../hooks/useAnalytics';

export default function Auth() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { conversion, track } = useAnalytics();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState('login');   // login | signup
  const [step, setStep] = useState('email');   // email | otp
  const [email, setEmail] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [showReferral, setShowReferral] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [brandLoading, setBrandLoading] = useState(false);
  const [branding, setBranding] = useState("");
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [signupSource, setSignupSource] = useState("organic");
  const emailRef = useRef(null);

  // ── Read referral code from URL: /auth?ref=ABC123 ─────────
  useEffect(() => {
    const ref = searchParams.get('ref');
    const src = searchParams.get('src');

    if (ref) {
      setReferralCode(ref.toUpperCase());
      setShowReferral(true);
      setMode('signup'); // auto-switch to signup if ref link
      track.viewSignUp(); // track referral-driven signup intent
    }

    if(src){
      setSignupSource(src);
      sessionStorage.setItem("signupSource", src)
    }
  }, []);

  useEffect(() => {
    getPublicAppSettings()
    .then(res => {
      if (!res.status) return;
      const { branding: rawBranding = {}, } = res.data;
      setBranding(rawBranding);
    })
    .catch(console.error)
    .finally(() => setBrandLoading(false));
  }, []);

  // Restore state on mount
useEffect(() => {
  const saved = sessionStorage.getItem('auth_flow_state');
  if (saved) {
    try {
      const { email: savedEmail, mode: savedMode, step: savedStep, expiresAt } = JSON.parse(saved);
      if (savedStep === 'otp' && expiresAt && expiresAt > Date.now()) {
        setEmail(savedEmail);
        setMode(savedMode);
        setStep('otp');
        setTimer(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
      } else {
        sessionStorage.removeItem('auth_flow_state');
      }
    } catch { sessionStorage.removeItem('auth_flow_state'); }
  }
}, []);

  // ── OTP countdown ─────────────────────────────────────────
  useEffect(() => {
    if (step !== 'otp') return;
    if (timer === 0) { setCanResend(true); return; }
    const id = setInterval(() => setTimer(t => t - 1), 1000);
    return () => clearInterval(id);
  }, [step, timer]);

  // ── Auto-focus ────────────────────────────────────────────
  useEffect(() => {
    if (step === 'email') setTimeout(() => emailRef.current?.focus(), 50);
    if (step === 'otp') setTimeout(() => document.getElementById('otp-0')?.focus(), 100);
  }, [step, mode]);

  // ── Mode switch ───────────────────────────────────────────
  const switchMode = (m) => {
    setMode(m);
    setStep('email');
    setError('');
    setOtp(['', '', '', '', '', '']);
    if (m === 'signup') track.viewSignUp();
  };

  // ── Google OAuth ──────────────────────────────────────────
  const handleGoogleLogin = () => {
    const ref = referralCode.trim();
    const src = signupSource || sessionStorage.getItem("signupSource") || "";
    const params = new URLSearchParams();
    if (ref) params.set("ref", ref);
    if (src) params.set("src", src);
    const qs = params.toString();
    window.location.href = `${base_url}/mtrade/auth/google${qs ? `?${qs}` : ""}`;
  };

  // ── Create user ───────────────────────────────────────────
  const handleCreateUser = async () => {
    const src = signupSource || sessionStorage.getItem("signupSource") || "organic";
    const data = await createUser(
      email, 
      'user', 
      referralCode.trim() || undefined,
      src
    );
    const user = login(data.token);
    // Fire signup conversion — new account created via OTP email flow
    conversion.signup(user?.userId || data?.userId);
    sessionStorage.removeItem("signupSource");
    navigate('/home');
  };

  // ── Send OTP ──────────────────────────────────────────────
  // Persist whenever we successfully move into the OTP step
const handleSendOTP = async (e) => {
  e.preventDefault();
  if (!email.trim()) return setError('Please enter your email');
  setIsLoading(true);
  setError('');
  try {
    const res = mode === 'login' ? await sendLoginOTP(email) : await receiveOTP(email);
    const expiresAt = res?.expiresAt || (Date.now() + 10 * 60 * 1000);
    setStep('otp');
    setTimer(30);
    setCanResend(false);
    setOtp(['', '', '', '', '', '']);
    sessionStorage.setItem('auth_flow_state', JSON.stringify({ email, mode, step: 'otp', expiresAt }));
  } catch (err) {
    setError(err.response?.data?.message || 'Failed to send code');
  } finally {
    setIsLoading(false);
  }
};

  // ── Verify OTP ────────────────────────────────────────────
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) return setError('Enter all 6 digits');
    setIsLoading(true);
    setError('');
    try {
      if (mode === 'login') {
        const pushToken = window.__pushToken || null;
        const data = await loginWithOTP(email, code, pushToken);
        login(data.token);
        sessionStorage.removeItem('auth_flow_state');
        track.pageView('/auth/otp/success', 'OTP Login Success');
        navigate('/home');
        return;
      }
      await verifyOTP(email, code);
      sessionStorage.removeItem('auth_flow_state');
      await handleCreateUser();
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code — try again');
      setOtp(['', '', '', '', '', '']);
      setTimeout(() => document.getElementById('otp-0')?.focus(), 50);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Resend ────────────────────────────────────────────────
  const handleResend = async () => {
    if (!canResend) return;
    setOtp(['', '', '', '', '', '']);
    setTimer(30);
    setCanResend(false);
    setError('');
    try {
      if (mode === 'login') await sendLoginOTP(email);
      else await receiveOTP(email);
    } catch { setError('Failed to resend code'); }
  };

  // ── OTP inputs ────────────────────────────────────────────
  const handleOtpChange = (i, val) => {
  const digits = val.replace(/\D/g, '');

  if (!digits) {
    const arr = [...otp]; arr[i] = ''; setOtp(arr);
    return;
  }

  if (digits.length === 1) {
    const arr = [...otp]; arr[i] = digits; setOtp(arr); setError('');
    if (i < 5) document.getElementById(`otp-${i + 1}`)?.focus();
    return;
  }

  // Multiple digits landed here (paste, SMS autofill, or password manager) —
  // spread them across boxes starting at the current index
  const arr = [...otp];
  let idx = i;
  for (const d of digits) {
    if (idx > 5) break;
    arr[idx] = d;
    idx++;
  }
  setOtp(arr);
  setError('');
  setTimeout(() => document.getElementById(`otp-${Math.min(idx - 1, 5)}`)?.focus(), 10);
};
  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0)
      document.getElementById(`otp-${i - 1}`)?.focus();
  };
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6).split('');
    const arr = [...otp];
    digits.forEach((d, i) => { arr[i] = d; });
    setOtp(arr);
    setTimeout(() => document.getElementById(`otp-${Math.min(digits.length - 1, 5)}`)?.focus(), 10);
  };

  const otpComplete = otp.every(d => d !== '');

  // ─────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: 'linear-gradient(160deg, #f0fdf4 0%, #ecfdf5 40%, #f0fdfa 100%)' }}
    >
      {/* Decorative background shapes */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, #6ee7b7 0%, transparent 70%)' }} />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #34d399 0%, transparent 70%)' }} />
        <div className="absolute top-1/2 right-8 w-48 h-48 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, #a7f3d0 0%, transparent 70%)' }} />
      </div>

      <div className="w-full max-w-[400px] relative z-10">

        {/* ── Brand mark ── */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg"
            >
            <img src={branding.logoUrl} alt="" />
          </div>
          <h1 className="text-slate-800 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {step === 'otp' ? 'Verify your email' : mode === 'login' ? 'Welcome back' : 'Create account'}
          </h1>
          <p className="text-slate-500 text-sm mt-1.5">
            {step === 'otp'
              ? <>Code sent to <span className="font-semibold text-slate-700">{email}</span></>
              : mode === 'login' ? 'Sign in to continue' : 'Join and start trading'}
          </p>
        </div>

        {/* ── Card ── */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden"
          style={{ boxShadow: '0 4px 6px rgba(0,0,0,0.04), 0 20px 60px rgba(16,185,129,0.1), 0 0 0 1px rgba(0,0,0,0.05)' }}>

          {/* Tab bar */}
          {step === 'email' && (
            <div className="flex border-b border-slate-100">
              {[['login', 'Sign In'], ['signup', 'Sign Up']].map(([m, label]) => (
                <button key={m} onClick={() => switchMode(m)}
                  className="flex-1 py-4 text-sm font-semibold transition-all relative"
                  style={{ color: mode === m ? '#059669' : '#94a3b8' }}>
                  {label}
                  {mode === m && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-emerald-500" />
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="p-7">
            {/* Error banner */}
            {error && (
              <div className="flex items-center gap-2.5 px-4 py-3 rounded-2xl mb-5 text-sm bg-red-50 border border-red-100">
                <i className="ri-error-warning-line text-red-500 flex-shrink-0" />
                <span className="text-red-600 font-medium">{error}</span>
              </div>
            )}

            {/* ── EMAIL STEP ── */}
            {step === 'email' && (
              <div className="space-y-4">
                {/* Google button */}
                <button onClick={handleGoogleLogin} type="button"
                  className="w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl text-sm font-semibold border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-all active:scale-[0.99] shadow-sm">
                  <svg width="18" height="18" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Continue with Google
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-100" />
                  <span className="text-slate-400 text-xs font-medium">or with email</span>
                  <div className="flex-1 h-px bg-slate-100" />
                </div>

                {/* Email input */}
                <form onSubmit={handleSendOTP} className="space-y-3">
                  <div className="relative">
                    <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-base" />
                    <input
                      ref={emailRef}
                      type="email"
                      value={email}
                      onChange={e => { setEmail(e.target.value); setError(''); }}
                      placeholder="your@email.com"
                      required
                      className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm text-slate-800 placeholder-slate-400 bg-slate-50 border border-slate-200 outline-none transition-all focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-50"
                    />
                  </div>

                  {/* Referral code — signup only */}
                  {mode === 'signup' && (
                    <div>
                      {!showReferral ? (
                        <button type="button" onClick={() => setShowReferral(true)}
                          className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium hover:text-emerald-700 transition-colors">
                          <i className="ri-gift-line" />
                          Have a referral code?
                        </button>
                      ) : (
                        <div className="relative">
                          <i className="ri-gift-line absolute left-4 top-1/2 -translate-y-1/2 text-emerald-400 text-base" />
                          <input
                            type="text"
                            value={referralCode}
                            onChange={e => setReferralCode(e.target.value.toUpperCase())}
                            placeholder="Referral code (optional)"
                            maxLength={20}
                            className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm text-slate-800 placeholder-slate-400 bg-emerald-50 border border-emerald-200 outline-none transition-all focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50 tracking-widest font-mono uppercase"
                          />
                          {referralCode && (
                            <div className="flex items-center gap-1 mt-1.5 px-1">
                              <i className="ri-checkbox-circle-fill text-emerald-500 text-xs" />
                              <span className="text-emerald-600 text-xs font-medium">Referral code applied</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <button type="submit" disabled={isLoading || !email.trim()}
                    className="w-full py-3.5 rounded-2xl text-sm font-bold text-white transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #059669, #10b981)', boxShadow: '0 4px 16px rgba(16,185,129,0.35)' }}>
                    {isLoading ? (
                      <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Sending code…</>
                    ) : (
                      <>{mode === 'login' ? 'Send sign-in code' : 'Send verification code'}<i className="ri-arrow-right-line" /></>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* ── OTP STEP ── */}
            {step === 'otp' && (
              <form onSubmit={handleVerifyOTP} className="space-y-5">
                {/* OTP boxes */}
                <div className="flex justify-center gap-2.5">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      id={`otp-${i}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete={i === 0 ? "one-time-code" : "off"}
                      maxLength={6}
                      value={digit}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      onPaste={handleOtpPaste}
                      className="text-center text-xl font-bold rounded-2xl outline-none transition-all duration-150 border-2"
                      style={{
                        width: '48px',
                        height: '56px',
                        background: digit ? '#f0fdf4' : '#f8fafc',
                        borderColor: digit ? '#10b981' : '#e2e8f0',
                        color: digit ? '#059669' : '#1e293b',
                        boxShadow: digit ? '0 0 0 4px rgba(16,185,129,0.1)' : 'none',
                      }}
                    />
                  ))}
                </div>

                {/* Timer / Resend */}
                <div className="text-center">
                  {canResend ? (
                    <button type="button" onClick={handleResend}
                      className="text-emerald-600 text-sm font-semibold hover:text-emerald-700 transition-colors">
                      Resend code
                    </button>
                  ) : (
                    <p className="text-slate-400 text-sm">
                      Resend code in <span className="font-semibold text-slate-600 tabular-nums">{timer}s</span>
                    </p>
                  )}
                </div>

                {/* Verify button */}
                <button type="submit" disabled={isLoading || !otpComplete}
                  className="w-full py-3.5 rounded-2xl text-sm font-bold text-white transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #059669, #10b981)', boxShadow: '0 4px 16px rgba(16,185,129,0.35)' }}>
                  {isLoading ? (
                    <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{mode === 'login' ? 'Signing in…' : 'Creating account…'}</>
                  ) : (
                    <><i className="ri-shield-check-line" />{mode === 'login' ? 'Sign In' : 'Create Account'}</>
                  )}
                </button>

                {/* Back */}
                <button type="button"
                  onClick={() => { setStep('email'); setError(''); }}
                  className="w-full flex items-center justify-center gap-1.5 text-slate-400 text-sm hover:text-slate-600 transition-colors py-1">
                  <i className="ri-arrow-left-line text-xs" /> Use a different email
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-400 text-xs mt-6 leading-relaxed">
          By continuing you agree to our{' '}
          <span className="text-slate-500 underline underline-offset-2 cursor-pointer hover:text-emerald-600 transition-colors">Terms of Service</span>
          {' '}and{' '}
          <span className="text-slate-500 underline underline-offset-2 cursor-pointer hover:text-emerald-600 transition-colors">Privacy Policy</span>
        </p>
      </div>
    </div>
  );
}
